import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, Chip, Button, IconButton, Tooltip,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, LinearProgress, Divider, Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";
import { isTenantAdmin } from "../../utils/entitlements";

const ROLES = [
  "tenant-admin",
  "enterprise-ai-owner",
  "tevv-lead",
  "risk-reviewer",
  "security-reviewer",
  "head-of-quality",
];
const ROLE_COLOR = {
  "tenant-admin":        { bg: "#e3f2fd", color: "#0d47a1" },
  "enterprise-ai-owner": { bg: "#e8f5e9", color: "#1b5e20" },
  "tevv-lead":           { bg: "#f3e5f5", color: "#6a1b9a" },
  "risk-reviewer":       { bg: "#fce4ec", color: "#b71c1c" },
  "security-reviewer":   { bg: "#fff8e1", color: "#f57f17" },
  "head-of-quality":     { bg: "#fff3e0", color: "#e65100" },
};

function RoleChip({ role }) {
  const style = ROLE_COLOR[role] || { bg: "#f5f5f5", color: "#424242" };
  return (
    <Chip label={role} size="small"
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, fontSize: "11px" }} />
  );
}

function UserFormDialog({ open, onClose, onSave, initial }) {
  const isEdit = Boolean(initial?.id);
  const initialRoles = Array.isArray(initial?.roles) && initial.roles.length
    ? initial.roles
    : (initial?.role ? [initial.role] : ["tevv-lead"]);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", roles: ["tevv-lead"], ...initial, roles: initialRoles,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const roles = Array.isArray(initial?.roles) && initial.roles.length
      ? initial.roles
      : (initial?.role ? [initial.role] : ["tevv-lead"]);
    setForm({ firstName: "", lastName: "", email: "", ...initial, roles });
    setErr("");
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.firstName && !isEdit) {
      setErr("First name, email and at least one role are required.");
      return;
    }
    if (!isEdit && (!form.email || !(form.roles || []).length)) {
      setErr("First name, email and at least one role are required.");
      return;
    }
    if (!isEdit && !String(form.lastName ?? "").trim()) {
      setErr("Last Name is required.");
      return;
    }
    if (!(form.roles || []).length) {
      setErr("Select at least one role.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || e.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{isEdit ? "Edit User Roles" : "Invite User"}</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {!isEdit && (
            <>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField size="small" label="First Name *" fullWidth value={form.firstName} onChange={set("firstName")} />
                <TextField size="small" label="Last Name *" fullWidth value={form.lastName} onChange={set("lastName")} />
              </Box>
              <TextField size="small" label="Email *" fullWidth value={form.email} onChange={set("email")} type="email" />
            </>
          )}
          {isEdit && (
            <Typography fontSize="14px" color="text.secondary">
              Updating roles for <strong>{initial?.email}</strong>
            </Typography>
          )}
          <FormControl size="small" fullWidth>
            <InputLabel>Roles *</InputLabel>
            <Select
              multiple
              value={form.roles || []}
              label="Roles *"
              onChange={(e) => setForm((f) => ({ ...f, roles: e.target.value }))}
              renderValue={(selected) => selected.join(", ")}
            >
              {ROLES.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography fontSize="12px" color="text.secondary">
            Users can hold multiple roles. Permissions are the union of all assigned roles.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
        >
          {saving ? "Saving…" : isEdit ? "Update Roles" : "Create User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CredentialsDialog({ open, onClose, creds }) {
  const [snack, setSnack] = useState("");
  const copy = (text) => navigator.clipboard.writeText(text).then(() => setSnack("Copied!"));

  // Mode 1: Email invite sent (real product / SMTP configured)
  if (creds?.emailSent) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: "#2e7d32" }} />
          Invitation Sent
        </DialogTitle>
        <DialogContent>
          <Typography fontSize="14px" color="text.secondary">
            A welcome email with login credentials has been sent to <strong>{creds.email}</strong>.
          </Typography>
          <Typography fontSize="13px" color="text.secondary" mt={1.5}>
            They can sign in with the temporary password provided and will be prompted to set a new password on first login. Role: <strong>{creds.role}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={onClose}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Mode 2: No SMTP — show credentials for manual sharing
  const fields = [
    { label: "Realm",              value: creds?.realm,        mono: true },
    { label: "Login URL",          value: creds?.loginUrl,     mono: true },
    { label: "Username",           value: creds?.username,     mono: true },
    { label: "Temporary Password", value: creds?.tempPassword, mono: true },
    { label: "Role",               value: creds?.role,         mono: false },
  ];

  const copyAll = () => {
    const text = fields.map(f => `${f.label}: ${f.value}`).join("\n")
      + "\n\nPlease log in and change your password immediately.";
    navigator.clipboard.writeText(text).then(() => setSnack("All credentials copied!"));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: "#2e7d32" }} />
          User Created — Share Credentials
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            SMTP not configured. Share these credentials manually. Temporary password is shown only once.
          </Alert>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {fields.map(({ label, value, mono }) => (
              <Box key={label}>
                <Typography fontSize="12px" color="text.secondary" mb={0.5}>{label}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{
                    flexGrow: 1, bgcolor: "#f5f5f5", border: "1px solid #e0e0e0",
                    borderRadius: 1, px: 1.5, py: 1,
                    fontFamily: mono ? "monospace" : "inherit",
                    fontSize: "13px", wordBreak: "break-all",
                  }}>
                    {value}
                  </Box>
                  <Tooltip title="Copy">
                    <IconButton size="small" onClick={() => copy(value)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography fontSize="12px" color="text.secondary">
            The user will be prompted to set a new password on first login.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyAll}
            sx={{ textTransform: "none", color: "#0094FB", borderColor: "#0094FB" }}>
            Copy All
          </Button>
          <Button variant="contained" onClick={onClose}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(snack)} autoHideDuration={2000} onClose={() => setSnack("")}
        message={snack} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </>
  );
}

export default function ClientUserManagementPage() {
  const { user } = useAuth();
  const canManageUsers = isTenantAdmin(user?.roles);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [tab, setTab] = useState(0); // 0=All 1=Active 2=Deactivated
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [credsDialog, setCredsDialog] = useState(null); // holds { username, tempPassword, loginUrl, role }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/client/users`),
        axios.get(`${API_BASE_URL}/client/stats`),
      ]);
      setUsers(usersRes.data.users || []);
      setStats(statsRes.data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    const payload = {
      firstName: form.firstName,
      lastName: String(form.lastName ?? "").trim(),
      email: form.email,
      roles: form.roles || [],
    };
    const res = await axios.post(`${API_BASE_URL}/client/users`, payload);
    load();

    const roleLabel = (res.data.roles || form.roles || []).join(", ");
    if (res.data.inviteEmailSent) {
      setCredsDialog({
        emailSent:   true,
        email:       form.email,
        role:        roleLabel,
      });
    } else {
      const realm = stats?.slug || localStorage.getItem("kc_realm") || "";
      const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN;
      const loginUrl = platformDomain
        ? `https://${realm}.${platformDomain}`
        : `${window.location.origin}?realm=${realm}`;
      setCredsDialog({
        emailSent:   false,
        username:    res.data.username,
        tempPassword: res.data.tempPassword,
        role:        roleLabel,
        realm,
        loginUrl,
      });
    }
  };

  const handleUpdateRole = async (form) => {
    await axios.put(`${API_BASE_URL}/client/users/${editUser.id}/role`, {
      roles: form.roles || [],
    });
    load();
  };

  const handleToggleStatus = async (u) => {
    await axios.put(`${API_BASE_URL}/client/users/${u.id}/status`, { enabled: !u.enabled });
    load();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove ${u.email} from this organisation?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/client/users/${u.id}`);
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to delete.");
    }
  };

  const tabFiltered = users.filter((u) => {
    if (tab === 1) return u.enabled;
    if (tab === 2) return !u.enabled;
    return true;
  });

  const filtered = tabFiltered.filter((u) => {
    const q = search.toLowerCase();
    const matchSearch = !q || u.email?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q);
    const matchRole = roleFilter === "all" || (u.roles || []).includes(roleFilter);
    return matchSearch && matchRole;
  });

  const usedPct = stats?.seat_count > 0
    ? Math.min(100, Math.round(((stats.seats_used || 0) / stats.seat_count) * 100))
    : 0;
  const overWarn = usedPct >= (stats?.warning_threshold || 80);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>User Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users and roles within your organisation
          </Typography>
        </Box>
        {canManageUsers && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditUser(null); setDialogOpen(true); }}
            sx={{ textTransform: "none", bgcolor: "#0094FB", "&:hover": { bgcolor: "#0080e0" } }}
          >
            Invite User
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {/* Seat usage banner */}
      {stats && (
        <Card elevation={0} sx={{
          border: `1px solid ${overWarn ? "#ed6c02" : "#e8e8e8"}`,
          borderRadius: 2, p: 2, mb: 3, bgcolor: overWarn ? "#fff8f0" : "#fff",
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography fontSize="13px" fontWeight={600}>Licence Usage — {usedPct}%</Typography>
            <Typography fontSize="12px" color="text.secondary">
              {stats.seats_used} / {stats.seat_count} seats
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={usedPct}
            sx={{ height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { bgcolor: overWarn ? "#ed6c02" : "#1976d2" } }}
          />
          {usedPct >= 100 && (
            <Typography fontSize="12px" color="error" mt={0.5}>
              Seat limit reached. Contact ValidAIte to expand your licence.
            </Typography>
          )}
        </Card>
      )}

      {/* Tabs + filters */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 36 }}
          TabIndicatorProps={{ style: { background: "#0094FB" } }}>
          <Tab label={`All (${users.length})`} sx={{ textTransform: "none", minHeight: 36, py: 0.5, fontSize: "13px" }} />
          <Tab label={`Active (${users.filter(u => u.enabled).length})`} sx={{ textTransform: "none", minHeight: 36, py: 0.5, fontSize: "13px" }} />
          <Tab label={`Deactivated (${users.filter(u => !u.enabled).length})`} sx={{ textTransform: "none", minHeight: 36, py: 0.5, fontSize: "13px" }} />
        </Tabs>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 220, bgcolor: "#fff" }}
          />
          <FormControl size="small" sx={{ minWidth: 140, bgcolor: "#fff" }}>
            <InputLabel>Role</InputLabel>
            <Select value={roleFilter} label="Role" onChange={(e) => setRoleFilter(e.target.value)}>
              <MenuItem value="all">All Roles</MenuItem>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              {["Name", "Email", "Username", "Role", "Status", "Created", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, fontSize: "12px", color: "#667085" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 500 }}>
                    {`${u.firstName || ""} ${u.lastName || ""}`.trim() || "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "12px" }}>{u.email || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "12px", fontFamily: "monospace" }}>{u.username || "—"}</TableCell>
                  <TableCell>
                    {(u.roles || []).length > 0
                      ? u.roles.map((r) => <RoleChip key={r} role={r} />)
                      : <Typography fontSize="12px" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.enabled ? "Active" : "Deactivated"}
                      size="small"
                      sx={{
                        bgcolor: u.enabled ? "#e8f5e9" : "#fafafa",
                        color: u.enabled ? "#2e7d32" : "#616161",
                        fontWeight: 600, fontSize: "11px",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "12px" }}>
                    {u.createdTimestamp ? new Date(u.createdTimestamp).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell>
                    {canManageUsers && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit Role">
                        <IconButton size="small" onClick={() => { setEditUser(u); setDialogOpen(true); }}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={u.enabled ? "Deactivate" : "Activate"}>
                        <IconButton size="small" onClick={() => handleToggleStatus(u)}>
                          {u.enabled
                            ? <PersonOffOutlinedIcon fontSize="small" sx={{ color: "#ed6c02" }} />
                            : <PersonOutlinedIcon fontSize="small" sx={{ color: "#2e7d32" }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove User">
                        <IconButton size="small" onClick={() => handleDelete(u)}>
                          <DeleteOutlineIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <UserFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={editUser ? handleUpdateRole : handleCreate}
        initial={editUser}
      />

      <CredentialsDialog
        open={Boolean(credsDialog)}
        onClose={() => setCredsDialog(null)}
        creds={credsDialog}
      />
    </Box>
  );
}
